/**
 * ============================================================
 *  操作指引 —— 菜单/页面引导 配置文件
 * ============================================================
 *
 *  本文件负责：
 *    1. 定义各「一级菜单」「二级菜单」的介绍文案（下方 MODULE_INTRO）；
 *    2. 根据当前用户可见菜单，自动生成引导步骤（buildMenuGuideSteps）；
 *    3. 记录「是否已引导过」，保证只在首次登录时弹出（localStorage 辅助函数）。
 *
 *  引导流程（自动生成）：
 *    步骤1  介绍一级菜单（高亮侧边栏一级菜单标题）
 *    步骤2  展开该一级菜单，介绍其下的二级菜单（高亮二级菜单项）
 *    步骤3  选中该二级菜单（自动跳转路由），居中展示介绍页面
 *    ... 依次介绍完所有二级菜单，再进入下一个一级菜单
 *
 *  —— 如何配置？只需改下面几处 ——
 *
 *  【第一步】在 MODULE_INTRO 里配置文案：
 *      key（引号里的文字）必须与侧边栏菜单名完全一致（后端 GrantName）。
 *      每个一级菜单是一个对象：
 *        content  : 一级菜单的整体介绍
 *        children : 该一级菜单下「二级菜单」的逐项介绍（key 为二级菜单名，可省略）
 *
 *  【第二步】按需调整开关：
 *      ENABLE_PAGE_INTRO : 是否生成"选中二级菜单进入页面"的介绍步骤（默认 true）
 *      PAGE_INTRO        : 页面介绍文案（可选覆盖，key 为菜单名）
 * ============================================================
 */

/**
 * 菜单介绍文案映射表
 * ------------------------------------------------------------------
 * 结构：
 *   {
 *     '一级菜单名': {
 *       content: '一级菜单整体介绍',
 *       children: {           // 可选：二级菜单逐项介绍
 *         '二级菜单名': '二级菜单功能介绍',
 *         ...
 *       }
 *     }
 *   }
 *
 * 说明：
 *   - content 用于「一级菜单」步骤的气泡文案；
 *   - children 里配置了的二级菜单，用配置文案；没配置的二级菜单会用默认文案兜底；
 *   - 一级菜单若未在 MODULE_INTRO 中出现，则整个模块（含其二级）都不会出现在引导里
 *     （除非开启 SHOW_MODULE_WITHOUT_INTRO）。
 *
 * 下面列出的只是示例，请按你系统里真实的菜单名修改/补充。
 */
export const MODULE_INTRO = {
  '权限管理': {
    content: '管理系统用户、角色及权限配置。',
    children: {
      '用户管理': '管理系统用户账号，可添加、编辑、删除、启停用及微信绑定等。',
      '角色管理': '管理系统角色及其权限。',
    },
  },
  '系统设置': {
    content: '系统各项功能与业务参数配置。',
    children: {
      '个性参数设置': '配置系统功能开关与业务参数。',
    },
  },
  '收费管理': {
    content: '收费相关类别、项目、套餐、规则及优惠的配置管理。',
    children: {
      '收费类别配置': '维护收费类别，可添加、编辑、查看、删除收费类别。',
      '收费项目配置': '维护检测服务收费项目，可添加、编辑、查看、删除及批量导入。',
      '收费套餐配置': '维护收费价格模板（套餐），可添加、编辑、删除及设为默认。',
      '收费规则配置': '维护收费规则，按车辆/检测等条件关联收费项目。',
      '收费优惠配置': '维护收费优惠设置，配置减免金额与审核方式。',
    },
  },
}

/**
 * 是否生成「选中二级菜单 → 进入页面」的介绍步骤
 *  - true（默认）：每个二级菜单之后追加一步"页面介绍"，会自动跳转路由展示页面
 *  - false       ：只介绍菜单项，不跳转页面
 */
export const ENABLE_PAGE_INTRO = false

/**
 * 页面介绍文案（可选）
 * key = 菜单名（一级或二级菜单名均可）
 * 不配置时用默认文案：这里是「xxx」功能页面。
 */
export const PAGE_INTRO = {
  // '订单登记': '这里是订单登记页面，填写车辆信息后即可生成检测订单。',
}

/**
 * 页面内元素介绍（可选，用于"进入页面后高亮某个筛选条件/按钮"）
 * ------------------------------------------------------------------
 * key = 二级菜单名（GrantName），与 MODULE_INTRO 里 children 的 key 一致
 * value = 元素介绍步骤数组，每项字段：
 *   selector  {String}  必填，页面内元素的 CSS 选择器（如 '[data-guide="车牌号输入框"]'）
 *   title     {String}  必填，气泡标题（一般写元素名，如"车牌号"）
 *   content   {String}  必填，气泡说明文字
 *   placement {String}  可选，气泡方向：top/bottom/left/right，默认 bottom
 *
 * 配置后，引导会在进入该页面后，逐个高亮这些元素进行介绍；
 * 未配置的页面则只做整体介绍（居中气泡）。
 *
 * 定位方式：需要先在目标页面元素上加上 data-guide="xxx" 属性，
 * 然后这里的 selector 写成 '[data-guide="xxx"]' 即可精确定位。
 */
export const PAGE_ELEMENTS = {
  // ============================================================
  //  权限管理 -> 用户管理
  // ============================================================
  '用户管理': [
    // —— 查询条件 ——
    { selector: '[data-guide="用户管理-关键字"]', title: '关键字', content: '按姓名 / 用户名 / 身份证号模糊查询用户。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-角色名称"]', title: '角色名称', content: '按角色筛选用户列表。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="用户管理-编辑按钮"]', title: '编辑', content: '选中一条用户后点击，可修改该用户信息。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-删除按钮"]', title: '删除', content: '选中一条用户后点击，可删除该用户。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-初始化密码"]', title: '初始化密码', content: '选中一条用户后点击，重置其登录密码。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-启用"]', title: '启用', content: '选中被禁用的用户后点击，恢复其账号使用。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-禁用"]', title: '禁用', content: '选中用户后点击，停用该账号。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-绑定微信"]', title: '绑定微信', content: '选中用户后点击，生成绑定二维码。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-解绑微信"]', title: '解绑微信', content: '选中用户后点击，解除其微信绑定。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-工作设置"]', title: '工作设置', content: '选中用户后点击，配置其工作相关设置。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-批量导入"]', title: '批量导入', content: '点击打开批量导入用户窗口。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="用户管理-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加用户窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：基本信息控件 ——
    { selector: '[data-guide="用户管理-弹窗-用户名"]', title: '用户名', content: '填写登录用户名。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-姓名"]', title: '姓名', content: '填写用户姓名。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-身份证明号码"]', title: '身份证明号码', content: '填写身份证号。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-手机号码"]', title: '手机号码', content: '填写手机号。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-邮箱"]', title: '邮箱', content: '填写邮箱地址。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-职位"]', title: '职位', content: '选择用户职位。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-驾驶本类别"]', title: '驾驶本类别', content: '选择驾驶本类别。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-家庭住址"]', title: '家庭住址', content: '填写家庭住址。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-查看车主电话"]', title: '查看车主电话', content: '设置是否允许该用户查看车主电话和地址。', placement: 'bottom' },
    { selector: '[data-guide="用户管理-弹窗-同步更新到会员"]', title: '同步更新到会员', content: '设置是否允许该用户同步更新到会员系统。', placement: 'bottom' },
    // —— 添加弹窗：角色分配（切换到「角色分配」标签页） ——
    { selector: '#tab-role', title: '角色分配', content: '点击切换到「角色分配」标签页，为用户分配角色。', placement: 'bottom', action: 'click', actionWait: 800 },
    { selector: '[data-guide="用户管理-弹窗-角色分配表格"]', title: '角色分配表格', content: '勾选要分配给该用户的角色。', placement: 'bottom' },
  ],

  // ============================================================
  //  权限管理 -> 角色管理
  // ============================================================
  '角色管理': [
    { selector: '[data-guide="角色管理-详情"]', title: '详情', content: '选中一条角色后点击，查看角色详情。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-编辑按钮"]', title: '编辑', content: '选中一条角色后点击，修改角色信息。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-删除按钮"]', title: '删除', content: '选中一条角色后点击，删除该角色。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-角色名称"]', title: '角色名称', content: '按角色名称查询。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-启用状态"]', title: '启用状态', content: '按启用状态筛选角色。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="角色管理-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加角色窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：控件 ——
    { selector: '[data-guide="角色管理-弹窗-角色名称"]', title: '角色名称', content: '填写角色名称。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-弹窗-启用状态"]', title: '启用状态', content: '选择角色启用状态。', placement: 'bottom' },
    { selector: '[data-guide="角色管理-弹窗-权限树"]', title: '功能权限', content: '勾选该角色拥有的功能权限（树状图）。', placement: 'bottom' },
  ],

  // ============================================================
  //  系统设置 -> 个性参数设置
  // ============================================================
  '个性参数设置': [
    // —— 功能授权 ——
    { selector: '#tab-auth', title: '功能授权', content: '配置系统各功能开关。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="个性参数-功能授权-是否开启预检"]', title: '是否开启预检', content: '是否启用预检功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启报告单打印"]', title: '开启报告单打印', content: '是否开启报告单打印功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启短信发送"]', title: '开启短信发送', content: '是否开启短信发送功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启消息提醒"]', title: '开启消息提醒', content: '是否开启消息提醒功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启设备预警"]', title: '开启设备预警', content: '是否开启设备预警功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启授权到期预警"]', title: '开启授权到期预警', content: '是否开启授权到期预警功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启设备管理"]', title: '开启设备管理', content: '是否开启设备管理功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启库存管理"]', title: '开启库存管理', content: '是否开启库存管理功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启考试功能"]', title: '开启考试功能', content: '是否开启考试功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启模拟考试功能"]', title: '开启模拟考试功能', content: '是否开启模拟考试功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启大厅叫号系统"]', title: '开启大厅叫号系统', content: '是否启用大厅叫号。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-车辆登记"]', title: '车辆登记', content: '是否开启车辆登记功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启交钥匙"]', title: '开启交钥匙', content: '是否开启交钥匙功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启库存入库审核"]', title: '开启库存入库审核', content: '是否开启库存入库审核。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-功能授权-开启库存出库审核"]', title: '开启库存出库审核', content: '是否开启库存出库审核。', placement: 'bottom' },

    // —— 收费功能设置 ——
    { selector: '#tab-charge', title: '收费功能设置', content: '配置收费相关功能与参数。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="个性参数-收费-是否禁用收费"]', title: '是否禁用收费', content: '是否禁用收费功能。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-未收费允许打印报告"]', title: '未收费允许打印报告', content: '未收费时是否允许打印报告。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-关闭收费OCR识别定位框"]', title: '关闭收费OCR识别定位框', content: '是否关闭收费OCR识别定位框。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-收费时必须选择收费套餐"]', title: '收费时必须选择收费套餐', content: '收费时是否必须选择收费套餐。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-收费时登记保险信息"]', title: '收费时登记保险信息', content: '收费时是否登记保险信息。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-允许自定义优惠金额"]', title: '允许自定义优惠金额', content: '开启后可自定义优惠金额。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启退费自动审核通过"]', title: '开启退费自动审核通过', content: '是否开启退费自动审核通过。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-允许先收费再预检"]', title: '允许先收费再预检', content: '是否允许先收费再预检。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启检测到期通知"]', title: '开启检测到期通知', content: '是否开启检测到期通知。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-到期提醒提前时间"]', title: '到期提醒提前时间', content: '设置到期提醒提前天数。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-默认评价超期时间"]', title: '默认评价超期时间', content: '设置默认评价超期时间。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启订单默认评价"]', title: '开启订单默认评价', content: '是否开启完成未评价订单的默认评价。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启检测完毕通知"]', title: '开启检测完毕通知', content: '是否启用检测完毕通知。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-自动设置检测完毕"]', title: '自动设置检测完毕', content: '是否自动设置检测完毕。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-关闭选择业务员"]', title: '关闭选择业务员', content: '是否关闭选择业务员。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启订单关联库存"]', title: '开启订单关联库存', content: '是否开启订单关联库存。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-会员绩效计算方式"]', title: '会员绩效计算方式', content: '选择会员绩效计算方式。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-大车多少"]', title: '大车多少(元/辆)', content: '设置按车辆数计算绩效时，大车的单车金额。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-小车多少"]', title: '小车多少(元/辆)', content: '设置按车辆数计算绩效时，小车的单车金额。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-收费-开启手机收款"]', title: '开启手机收款', content: '开启后支持手机扫码收款。', placement: 'bottom' },

    // —— 预检功能设置 ——
    { selector: '#tab-precheck', title: '预检功能设置', content: '配置预检相关功能。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="个性参数-预检-是否显示预检完成记录"]', title: '是否显示预检完成记录', content: '是否显示预检完成记录。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-预检-预检开启召回提示"]', title: '预检开启召回提示', content: '预检是否开启召回提示。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-预检-预检车主电话可为空"]', title: '预检车主电话可为空', content: '预检时车主电话是否可为空。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-预检-预检登记环保信息"]', title: '预检登记环保信息', content: '预检是否登记环保信息。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-预检-限制预检员手机操作权限"]', title: '限制预检员手机操作权限', content: '是否限制预检员手机操作权限。', placement: 'bottom' },

    // —— 微信绑定设置 ——
    { selector: '#tab-miniapp', title: '微信绑定设置', content: '配置微信小程序码绑定相关功能。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="个性参数-微信-绑定员工用小程序码"]', title: '绑定员工用小程序码', content: '是否允许用小程序码绑定员工。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-微信-绑定会员用小程序码"]', title: '绑定会员用小程序码', content: '是否允许用小程序码绑定会员。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-微信-会员推广用小程序码"]', title: '会员推广用小程序码', content: '是否使用会员推广小程序码。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-微信-绑定车主使用小程序码"]', title: '绑定车主使用小程序码', content: '是否允许用小程序码绑定车主。', placement: 'bottom' },

    // —— 其他设置 ——
    { selector: '#tab-other', title: '其他设置', content: '配置其他参数。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="个性参数-其他-车牌号前缀"]', title: '车牌号前缀', content: '设置车牌号前缀。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-其他-使用百度OCR识别身份证"]', title: '使用百度OCR识别身份证', content: '是否使用百度OCR识别身份证。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-其他-车辆档案显示车主电话"]', title: '车辆档案显示车主电话', content: '车辆档案是否显示车主电话。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-其他-开启员工身份证号必填"]', title: '开启员工身份证号必填', content: '是否开启员工身份证号必填。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-其他-开启会员身份证号必填"]', title: '开启会员身份证号必填', content: '是否开启会员身份证号必填。', placement: 'bottom' },
    { selector: '[data-guide="个性参数-其他-报告单密钥"]', title: '报告单密钥', content: '查看用于报告单加密的密钥。', placement: 'bottom' },
  ],

  // ============================================================
  //  收费管理 -> 收费类别配置
  // ============================================================
  '收费类别配置': [
    // —— 查询条件 ——
    { selector: '[data-guide="收费类别配置-收费类别"]', title: '收费类别', content: '按收费类别名称模糊查询。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="收费类别配置-编辑按钮"]', title: '编辑', content: '选中一条收费类别后点击，修改收费类别信息。', placement: 'bottom' },
    { selector: '[data-guide="收费类别配置-查看按钮"]', title: '查看', content: '选中一条收费类别后点击，查看收费类别详情。', placement: 'bottom' },
    { selector: '[data-guide="收费类别配置-删除按钮"]', title: '删除', content: '选中一条收费类别后点击，删除该收费类别。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="收费类别配置-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加收费类别窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：控件 ——
    { selector: '[data-guide="收费类别配置-弹窗-收费类别名称"]', title: '收费类别名称', content: '填写收费类别名称。', placement: 'bottom' },
    { selector: '[data-guide="收费类别配置-弹窗-备注"]', title: '备注', content: '填写收费类别备注信息。', placement: 'bottom' },
  ],

  // ============================================================
  //  收费管理 -> 收费项目配置
  // ============================================================
  '收费项目配置': [
    // —— 查询条件 ——
    { selector: '[data-guide="收费项目配置-服务名称"]', title: '服务名称', content: '按服务名称模糊查询。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-收费类型"]', title: '收费类型', content: '按收费类型筛选服务列表。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="收费项目配置-编辑按钮"]', title: '编辑', content: '选中一条服务后点击，修改服务信息。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-查看按钮"]', title: '查看', content: '选中一条服务后点击，查看服务详情。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-删除按钮"]', title: '删除', content: '选中服务后点击，删除所选服务。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-批量导入按钮"]', title: '批量导入', content: '点击打开批量导入服务窗口。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="收费项目配置-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加服务窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：控件 ——
    { selector: '[data-guide="收费项目配置-弹窗-服务名称"]', title: '服务名称', content: '填写服务名称。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-服务价格"]', title: '服务价格', content: '填写服务价格（元/车次）。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-收费类型"]', title: '收费类型', content: '选择收费类型。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-检测类型"]', title: '检测类型', content: '选择检测类型。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-车辆类别"]', title: '车辆类别', content: '选择车辆类别。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-号牌种类"]', title: '号牌种类', content: '选择号牌种类。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-是否启用"]', title: '是否启用', content: '设置服务是否启用。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-服务详情"]', title: '服务详情', content: '填写服务详情说明。', placement: 'bottom' },
    { selector: '[data-guide="收费项目配置-弹窗-服务须知"]', title: '服务须知', content: '填写服务须知说明。', placement: 'bottom' },
  ],

  // ============================================================
  //  收费管理 -> 收费套餐配置
  // ============================================================
  '收费套餐配置': [
    // —— 查询条件 ——
    { selector: '[data-guide="收费套餐配置-模板名称"]', title: '模板名称', content: '按模板名称模糊查询。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="收费套餐配置-编辑按钮"]', title: '编辑', content: '选中一条模板后点击，修改模板信息。', placement: 'bottom' },
    { selector: '[data-guide="收费套餐配置-删除按钮"]', title: '删除', content: '选中模板后点击，删除所选模板。', placement: 'bottom' },
    { selector: '[data-guide="收费套餐配置-设为默认按钮"]', title: '设为默认', content: '选中一条模板后点击，将其设为默认价格模板。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="收费套餐配置-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加模板窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：模板信息 ——
    { selector: '[data-guide="收费套餐配置-弹窗-模板名称"]', title: '模板名称', content: '填写模板名称。', placement: 'bottom' },
    { selector: '[data-guide="收费套餐配置-弹窗-服务总价格"]', title: '服务总价格', content: '由所选关联服务自动汇总总价格。', placement: 'bottom' },
    { selector: '[data-guide="收费套餐配置-弹窗-排序"]', title: '排序', content: '填写模板显示排序序号。', placement: 'bottom' },
    { selector: '[data-guide="收费套餐配置-弹窗-备注"]', title: '备注', content: '填写模板备注信息。', placement: 'bottom' },
    // —— 添加弹窗：关联服务表格（先切换到「关联服务」标签页） ——
    { selector: '#tab-service', title: '关联服务', content: '点击切换到「关联服务」标签页，选择要关联的服务。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="收费套餐配置-弹窗-关联服务表格"]', title: '关联服务表格', content: '勾选要关联到该套餐的服务项目，总价格会自动汇总。', placement: 'bottom' },
  ],

  // ============================================================
  //  收费管理 -> 收费规则配置
  // ============================================================
  '收费规则配置': [
    // —— 查询条件 ——
    { selector: '[data-guide="收费规则配置-规则名称"]', title: '规则名称', content: '按规则名称模糊查询。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="收费规则配置-编辑按钮"]', title: '编辑', content: '选中一条规则后点击，修改规则信息。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-查看按钮"]', title: '查看', content: '选中一条规则后点击，查看规则详情。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-删除按钮"]', title: '删除', content: '选中规则后点击，删除所选规则。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="收费规则配置-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加规则窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：基础信息 ——
    { selector: '[data-guide="收费规则配置-弹窗-规则名称"]', title: '规则名称', content: '填写规则名称。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-合计金额"]', title: '合计金额', content: '由所选关联服务自动汇总合计金额。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-检测类型"]', title: '检测类型', content: '选择适用的检测类型。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-车辆类别"]', title: '车辆类别', content: '选择适用的车辆类别。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-燃油类型"]', title: '燃油类型', content: '选择适用的燃油类型。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-使用性质"]', title: '使用性质', content: '选择适用的使用性质。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-号牌种类"]', title: '号牌种类', content: '选择适用的号牌种类。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-轴数"]', title: '轴数', content: '填写适用的轴数。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-总质量"]', title: '总质量', content: '填写适用的总质量（Kg）。', placement: 'bottom' },
    { selector: '[data-guide="收费规则配置-弹窗-是否启用"]', title: '是否启用', content: '设置规则是否启用。', placement: 'bottom' },
    // —— 添加弹窗：关联服务表格（先切换到「关联服务」标签页） ——
    { selector: '#tab-service', title: '关联服务', content: '点击切换到「关联服务」标签页，选择要关联的服务。', placement: 'bottom', action: 'click', actionWait: 400 },
    { selector: '[data-guide="收费规则配置-弹窗-关联服务表格"]', title: '关联服务表格', content: '勾选要关联到该规则的服务项目，合计金额会自动汇总。', placement: 'bottom' },
  ],

  // ============================================================
  //  收费管理 -> 收费优惠配置
  // ============================================================
  '收费优惠配置': [
    // —— 查询条件 ——
    { selector: '[data-guide="收费优惠配置-优惠名称"]', title: '优惠名称', content: '按优惠名称模糊查询。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-启用状态"]', title: '启用状态', content: '按启用/停用状态筛选优惠。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-创建时间"]', title: '创建时间', content: '按创建时间范围筛选优惠。', placement: 'bottom' },
    // —— 操作按钮 ——
    { selector: '[data-guide="收费优惠配置-编辑按钮"]', title: '编辑', content: '选中一条优惠后点击，修改优惠设置。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-删除按钮"]', title: '删除', content: '选中优惠后点击，删除所选优惠。', placement: 'bottom' },
    // —— 添加按钮（自动点击打开添加弹窗） ——
    { selector: '[data-guide="收费优惠配置-添加按钮"]', title: '添加', content: '点击「添加」按钮，弹出添加优惠设置窗口。', placement: 'bottom', action: 'click', actionWait: 600 },
    // —— 添加弹窗：控件 ——
    { selector: '[data-guide="收费优惠配置-弹窗-优惠名称"]', title: '优惠名称', content: '填写优惠名称。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-减免金额"]', title: '减免金额(元)', content: '填写减免金额。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-自动审核通过"]', title: '自动审核通过', content: '设置是否自动审核通过。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-先收费后审核"]', title: '先收费后审核', content: '设置是否先收费后审核。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-排序序号"]', title: '排序序号', content: '填写显示排序序号。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-是否启用"]', title: '是否启用', content: '设置优惠是否启用。', placement: 'bottom' },
    { selector: '[data-guide="收费优惠配置-弹窗-备注"]', title: '备注', content: '填写备注信息。', placement: 'bottom' },
  ],
}

/**
 * 是否对"未在 MODULE_INTRO 中配置"的一级菜单也生成引导
 *  - false（当前默认）：只引导 MODULE_INTRO 里配置过的模块，更精准可控
 *  - true：所有可见一级菜单都引导，未配置的用默认文案兜底
 */
export const SHOW_MODULE_WITHOUT_INTRO = false

/**
 * localStorage 键名前缀
 * 实际存储时会拼接用户编号（UserCode），
 * 这样【每个账号首次登录】都会引导一次，而不是整个浏览器只引导一次。
 */
const STORAGE_KEY_PREFIX = 'YANTONG_GUIDE_HOME_DONE_'

/** 默认文案：一级菜单未配置 content 时使用 */
function defaultIntro(name) {
  return `点击进入「${name}」模块`
}

/** 默认文案：二级菜单未配置 children 文案时使用 */
function defaultChildIntro(name) {
  return `点击进入「${name}」功能模块`
}

/** 获取某个二级菜单的介绍文案 */
function getChildIntro(menuName, childName) {
  const menu = MODULE_INTRO[menuName]
  if (menu && menu.children && menu.children[childName]) {
    return menu.children[childName]
  }
  return defaultChildIntro(childName)
}

/** 获取页面介绍文案 */
function getPageIntro(name) {
  return PAGE_INTRO[name] || `这里是「${name}」功能页面，可以进行相关操作。`
}

/**
 * 追加某个页面的"元素级介绍"步骤
 * 若该页面在 PAGE_ELEMENTS 中配置了元素，则逐个生成 element 步骤。
 * @param {Array} steps 步骤数组（会被直接修改）
 * @param {string} name 二级菜单名（PAGE_ELEMENTS 的 key）
 * @param {string} path 页面路由路径
 */
function pushPageElementSteps(steps, name, path) {
  const elements = PAGE_ELEMENTS[name]
  if (!elements || !elements.length) return
  elements.forEach((el) => {
    steps.push({
      type: 'element', // 高亮页面内元素
      selector: el.selector,
      title: el.title,
      content: el.content,
      placement: el.placement || 'bottom',
      route: path, // 跳转到该页面
      action: el.action, // 可选：'click' 定位后自动点击（打开弹窗/切换标签页）
      actionWait: el.actionWait, // 可选：自动点击后等待时长(ms)，默认 600
    })
  })
}

/**
 * 生成「菜单/页面」引导步骤
 * ------------------------------------------------------------------
 * 遍历当前用户可见的一级菜单，按以下规则生成步骤：
 *   - 多级菜单（children > 1，即 el-submenu）：
 *       ① 一级菜单介绍（高亮标题）
 *       ② 逐个二级菜单介绍（展开一级菜单后高亮二级菜单项）
 *       ③ 每个二级菜单后追加"页面介绍"（跳转路由，居中气泡）—— 若开启
 *   - 单子菜单（children == 1，即直接是菜单项）：
 *       ① 菜单介绍（高亮菜单项）
 *       ② 页面介绍（跳转路由）—— 若开启
 *
 * 依赖：侧边栏菜单项在 index.vue 中已加 data-guide="菜单名" 属性。
 *
 * @param {Object} router Vue Router 实例（this.$router）
 * @returns {Array} 引导步骤数组，可直接传给 <GuideTour :steps="...">
 */
export function buildMenuGuideSteps(router) {
  const steps = []
  const routes = router.options.routes || []

  routes.forEach((item, index) => {
    // 过滤：隐藏菜单、非导航菜单、无子菜单的项
    if (item.hidden) return
    if (item.IsNotNavigation) return
    if (!item.children || item.children.length === 0) return

    const menuName = item.name
    const intro = MODULE_INTRO[menuName]

    // 未配置文案、且不开启兜底开关时，跳过整个一级菜单
    if (!intro && !SHOW_MODULE_WITHOUT_INTRO) return

    // 过滤出可见的二级菜单
    let visibleChildren = item.children.filter((c) => !c.hidden)

    // 若该一级菜单已配置了 children（或 PAGE_ELEMENTS），
    // 则只介绍被明确配置的二级菜单，避免把同一级菜单下未要求介绍的其他子菜单也一起引导。
    if (intro && intro.children) {
      visibleChildren = visibleChildren.filter(
        (c) => intro.children[c.name] || PAGE_ELEMENTS[c.name]
      )
    }

    // 情况一：多级菜单（el-submenu，需展开后介绍二级）
    if (item.children.length > 1) {
      // ① 一级菜单介绍
      steps.push({
        type: 'menu',
        selector: `[data-guide="${menuName}"]`,
        title: menuName,
        content: (intro && intro.content) || defaultIntro(menuName),
        placement: 'right',
      })

      // ② 逐个二级菜单介绍 + ③ 页面介绍
      visibleChildren.forEach((child) => {
        steps.push({
          type: 'submenu',
          selector: `[data-guide="${child.name}"]`,
          title: child.name,
          content: getChildIntro(menuName, child.name),
          placement: 'right',
          openMenuIndex: String(index), // 定位前展开对应一级菜单
        })

        if (ENABLE_PAGE_INTRO && child.path) {
          steps.push({
            type: 'page',
            mode: 'center', // 居中气泡，不高亮页面元素
            title: child.name,
            content: getPageIntro(child.name),
            route: child.path, // 跳转到该二级菜单页面
          })
        }

        // ④ 页面内元素介绍（若该页面配置了 PAGE_ELEMENTS）
        pushPageElementSteps(steps, child.name, child.path)
      })
    } else {
      // 情况二：单子菜单（一级菜单本身就是可点击菜单项）
      const child = item.children[0]
      steps.push({
        type: 'menu',
        selector: `[data-guide="${menuName}"]`,
        title: menuName,
        content: (intro && intro.content) || defaultIntro(menuName),
        placement: 'right',
      })

      if (ENABLE_PAGE_INTRO && child.path) {
        steps.push({
          type: 'page',
          mode: 'center',
          title: menuName,
          content: getPageIntro(menuName),
          route: child.path,
        })
      }

      // 页面内元素介绍（若该页面配置了 PAGE_ELEMENTS）
      pushPageElementSteps(steps, menuName, child.path)
    }
  })

  return steps
}

/**
 * 判断当前用户是否已完成过引导
 * @param {string} userCode 用户编号（this.$store.getters['user/UserInfo'].UserCode）
 * @returns {boolean} true=已引导过（不再弹），false=未引导（需要弹）
 */
export function hasGuidedHome(userCode) {
  return localStorage.getItem(STORAGE_KEY_PREFIX + userCode) === '1'
}

/**
 * 标记当前用户已完成引导
 * @param {string} userCode 用户编号
 */
export function markGuidedHome(userCode) {
  localStorage.setItem(STORAGE_KEY_PREFIX + userCode, '1')
}

/**
 * 清除当前用户的引导标记（用于测试，或"重新查看引导"时先清掉再打开）
 * @param {string} userCode 用户编号
 */
export function clearGuidedHome(userCode) {
  localStorage.removeItem(STORAGE_KEY_PREFIX + userCode)
}



// WEBPACK FOOTER //
// ./src/components/guide/guideConfig.js